'use client';

import { useRouter } from '@bprogress/next/app';
import { useSelector } from '@xstate/store/react';
import { usePathname } from 'next/navigation';
import { useState, useEffect } from 'react';

import { GTMPageView } from '@/fo/api/gtm';
import { ProductStockStatus, getSessionValue } from '@/fo/api/magento';
import {
  getStoresList,
  type GetStoresResponse,
  type StorePoint
} from '@/fo/feature/shops/page/base';
import {
  selectCountProductInCart,
  selectCountProductInWishlist,
  storeProduct,
  cartActions,
  wishlistActions,
  selectWishlistItemId,
  selectCartItemId,
  selectCartItemLoading,
  selectCompareProducts,
  compareActions
} from '@/fo/store/product';
import { storeProductVariant } from '@/fo/store/product-variant';
import {
  UiProductInfoMain,
  UiProductInfoMainProps
} from '@/fo/ui/product/info/main';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';
import { MappedProduct } from '@/fo/util/map-product-data';

import {
  getProductDelivery,
  type GetProductDeliveryResponse
} from './getProductDelivery';
export interface FeatureProductInfoMainProps
  extends Omit<
    UiProductInfoMainProps,
    | 'isInWishlist'
    | 'cartCount'
    | 'isCartLoading'
    | 'onOptionChange'
    | 'isInCompare'
  > {
  isAuthorized: boolean;
  goToDescription?: () => void;
  listName: 'product-detail-page' | 'preview';
  onSelectCity?: (city: string) => void;
  storesData?: {
    stores: StorePoint[];
    error?: string | null;
    productDeliveryData:
      | GetProductDeliveryResponse['productDeliveryData']
      | null;
  };
}

export function FeatureProductInfoMain({
  isAuthorized,
  goToDescription,
  baseProduct,
  ...props
}: FeatureProductInfoMainProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [selectedProduct, setSelectedProduct] = useState(props.selectedProduct);
  const [shippingCity, setShippingCity] = useState('');
  const [deliveryData, setDeliveryData] = useState<
    GetStoresResponse & GetProductDeliveryResponse
  >({ stores: [], productDeliveryData: null });
  const [isLoading, setIsLoading] = useState(false);
  const isConfigurableProduct =
    Array.isArray(selectedProduct.originalConfigurableVariants) &&
    selectedProduct.originalConfigurableVariants.length > 0;

  const cartCount = useSelector(
    storeProduct,
    selectCountProductInCart(selectedProduct.sku)
  );
  const cartItemId = useSelector(
    storeProduct,
    selectCartItemId(selectedProduct.sku)
  );
  const cartLoading = useSelector(
    storeProduct,
    selectCartItemLoading(selectedProduct.sku)
  );
  const wishlistCount = useSelector(
    storeProduct,
    selectCountProductInWishlist(selectedProduct.sku)
  );
  const wishlistItemId = useSelector(
    storeProduct,
    selectWishlistItemId(selectedProduct.sku)
  );

  const compareItemId = useSelector(
    storeProduct,
    selectCompareProducts(selectedProduct.sku)
  );

  const onStockClick = () => {
    // setIsStockModalOpen(true);
  };

  // const onStockModalClose = () => {
  //   setIsStockModalOpen(false);
  // };

  const isInWishlist = wishlistCount > 0;
  const isInCompare = compareItemId !== '';
  const isInCart = cartCount > 0;

  const price = selectedProduct.finalPrice ?? selectedProduct.basePrice ?? 0;

  const onCartClick = () => {
    if (selectedProduct.stockStatus === ProductStockStatus.OutOfStock) {
      return;
    }

    if (isInCart) {
      storeProductVariant.send({ type: 'closeModal' });
      router.push('/cart');
    } else {
      cartActions.addProductsToCart([
        {
          sku: selectedProduct.sku,
          quantity: 1,
          product: selectedProduct
        }
      ]);

      if (
        pathname === '/product/besprovodnoy-pylesos-dreame-r10s-essential-aqua/'
      ) {
        const img = new Image();

        img.src =
          'https://ad.new-programmatic.com/conv?aid=12479&convid=10931&random=##RANDOM=##';
      }
    }
  };

  const onCartChange = async (quantity: number) => {
    if (!cartItemId) return;

    if (quantity === 0) {
      await cartActions.removeCartItem(
        selectedProduct.sku,
        cartItemId,
        selectedProduct
      );
    } else {
      await cartActions.updateCartItem(
        selectedProduct.sku,
        cartItemId,
        quantity,
        selectedProduct
      );
    }
  };

  const onWishlistClick = () => {
    if (isAuthorized) {
      if (isInWishlist && wishlistItemId) {
        wishlistActions.removeWishlistItems([
          {
            sku: selectedProduct.parentSku || selectedProduct.sku,
            wishlistItemId
          }
        ]);
      } else {
        wishlistActions.addProductToWishlist(
          selectedProduct.sku,
          1,
          selectedProduct.parentSku
        );
        if (
          pathname ===
          '/product/besprovodnoy-pylesos-dreame-r10s-essential-aqua/'
        ) {
          const img = new Image();

          img.src =
            'https://ad.new-programmatic.com/conv?aid=12479&convid=10932&random=##RANDOM=##';
        }
      }
    } else {
      utilEventAuthModal.open('wishlist-button');
    }
  };

  const onCompareClick = () => {
    if (isAuthorized) {
      if (isInCompare) {
        compareActions.removeProductFromCompare(selectedProduct.id);
      } else {
        compareActions.addProductToCompare(selectedProduct.id, {
          name: selectedProduct.name,
          imageUrl: selectedProduct.baseImage?.src ?? ''
        });
        if (
          pathname ===
          '/product/besprovodnoy-pylesos-dreame-r10s-essential-aqua/'
        ) {
          const img = new Image();

          img.src =
            'https://ad.new-programmatic.com/conv?aid=12479&convid=10933&random=##RANDOM=##';
        }
      }
    } else {
      utilEventAuthModal.open('compare-button');
    }
  };

  const handleOptionChange = (product: MappedProduct | null) => {
    if (!product) {
      // storeProductVariant.send({
      //   type: 'setSelectedProduct',
      //   product: baseProduct
      // });
      return;
    }

    setSelectedProduct(product);

    // storeProductVariant.send({ type: 'setSelectedProduct', product });
  };

  const onSelectCity = (city: string) => {
    setShippingCity(city);
    setIsLoading(true);
  };

  useEffect(() => {
    const fetchStores = async () => {
      const currentCity =
        shippingCity !== ''
          ? shippingCity
          : await getSessionValue('LOCATION_CITY');
      const cityName = currentCity ? JSON.parse('"' + currentCity + '"') : '';
      const data = await getStoresList(cityName, selectedProduct.sku);
      const deliveryData = await getProductDelivery(
        cityName,
        selectedProduct.sku
      );
      setDeliveryData({ ...data, ...deliveryData });
      setIsLoading(false);
    };
    fetchStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingCity]);
  return (
    <>
      <GTMPageView
        key={selectedProduct.sku}
        event="detail"
        value={String(
          selectedProduct.finalPrice ?? selectedProduct.basePrice ?? ''
        )}
        items={[
          {
            id: selectedProduct.sku,
            name: selectedProduct.name,
            price,
            brand: selectedProduct.brand,
            category: !(
              selectedProduct.lastLevelCategory &&
              selectedProduct.lastLevelCategory.breadcrumbs
            )
              ? selectedProduct.brand
              : (selectedProduct.lastLevelCategory.name ?? '')
          }
        ]}
      />
      <UiProductInfoMain
        {...props}
        selectedProduct={selectedProduct}
        baseProduct={baseProduct}
        goToDescription={goToDescription}
        isAuthorized={isAuthorized}
        isInWishlist={isInWishlist}
        isInCompare={isInCompare}
        isCartLoading={cartLoading}
        cartCount={cartCount}
        onCartClick={onCartClick}
        onCartChange={onCartChange}
        onWishlistClick={onWishlistClick}
        onCompareClick={onCompareClick}
        onOptionChange={handleOptionChange}
        onStockClick={onStockClick}
        isConfigurableProduct={isConfigurableProduct}
        storesData={deliveryData}
        onSelectCity={onSelectCity}
        isLoading={isLoading}
      />
      {/*<FeatureProductModalStock
        sku={selectedProduct.sku}
        open={isStockModalOpen}
        onOpenChange={onStockModalClose}
      />*/}
    </>
  );
}
